import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";

import { PageHeader, SectionCard, ThemedDataGrid } from "_shared/ui";
import { toastMessage } from "utils/toastMessage";
import {
  getRolesList,
  createRolePermission,
  editRolePermission,
  deleteRolePermission,
  getSeAnalyticsFilters,
} from "_controllers/officer";

import { DASHBOARD_PAGES, SE_ANALYTICS_API_RESOURCE, pageResource } from "config/dashboardPages";

const ZONE_LABELS = { WZ: "West", NZ: "North", SZ: "South", EZ: "East", CZ: "Central" };
const zoneLabel = (z) => ZONE_LABELS[z] || z;

// Zone/region scope is only relevant when the SE dashboard is granted. The
// permission matrix is keyed by pageResource() (API path when defined), so the
// SE-analytics key is its API resource.
const SE_RESOURCE = SE_ANALYTICS_API_RESOURCE;
const EMPTY_SCOPE = { allZones: false, zones: [], allRegions: false, regions: [] };

const emptyForm = {
  _id: null,
  SAP_ID: "",
  email: "",
  password: "",
  roleName: "",
  scope: { ...EMPTY_SCOPE },
  permissions: {}, // { [resource]: { view: bool, edit: bool } }
};

/**
 * Reusable Roles & Permissions manager.
 *
 * @param resourceOptions - the grantable pages shown in the permission matrix.
 *                          Defaults to all dashboard pages; pass SE_PAGES to
 *                          scope an SE-admin to SE pages only.
 * @param title / subtitle - page header text.
 */
const RolesManager = ({
  resourceOptions = DASHBOARD_PAGES,
  title = "Roles & Permissions",
  subtitle = "Assign dashboard access and zone / region data scope to a SAP ID",
}) => {
  const RESOURCE_OPTIONS = resourceOptions;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [searchApplied, setSearchApplied] = useState("");

  const [zoneOptions, setZoneOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);

  const [form, setForm] = useState(emptyForm);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // row pending delete

  // Load zone/region options for the scope selectors.
  useEffect(() => {
    (async () => {
      try {
        const data = await getSeAnalyticsFilters();
        if (data) {
          setZoneOptions(data.zones || []);
          setRegionOptions(data.regions || []);
        }
      } catch (err) {
        console.log("filter options error", err);
      }
    })();
  }, []);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchApplied(search);
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const res = await getRolesList({ page: page + 1, limit: pageSize, search: searchApplied });
      setRows(res?.allroles || []);
      setTotal(res?.totalcounts || 0);
    } catch (err) {
      console.log("roles list error", err);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, [page, pageSize, searchApplied]);

  const openCreate = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    const permissions = {};
    (row.permissions || []).forEach((p) => {
      permissions[p.resource] = {
        view: (p.actions || []).includes("view"),
        edit: (p.actions || []).includes("edit"),
      };
    });
    setForm({
      _id: row._id,
      SAP_ID: row.SAP_ID || "",
      email: row.email || "",
      password: "",
      roleName: row.roleName || "",
      scope: {
        allZones: row.scope?.allZones || false,
        zones: row.scope?.zones || [],
        allRegions: row.scope?.allRegions || false,
        regions: row.scope?.regions || [],
      },
      permissions,
    });
    setDialogOpen(true);
  };

  const setScope = (patch) => setForm((f) => ({ ...f, scope: { ...f.scope, ...patch } }));

  const togglePermission = (resource, action) =>
    setForm((f) => {
      const current = f.permissions[resource] || { view: false, edit: false };
      return {
        ...f,
        permissions: { ...f.permissions, [resource]: { ...current, [action]: !current[action] } },
      };
    });

  // Convert the form's permission map into the API shape (only granted rows).
  const buildPermissionsPayload = () =>
    Object.entries(form.permissions)
      .map(([resource, a]) => {
        const actions = [];
        if (a.view) actions.push("view");
        if (a.edit) actions.push("edit");
        return { resource, actions };
      })
      .filter((p) => p.actions.length > 0);



  const handleSave = async () => {
    if (!form.SAP_ID.trim() || !form.email.trim() || !form.roleName.trim()) {
      toastMessage("SAP ID, Email and Role Name are required.", "error");
      return;
    }
    if (!form._id && !form.password.trim()) {
      toastMessage("Password is required to create a sub-admin login.", "error");
      return;
    }
    const permissions = buildPermissionsPayload();
    if (permissions.length === 0) {
      toastMessage("Grant at least one page permission.", "error");
      return;
    }
    const payload = {
      SAP_ID: form.SAP_ID.trim(),
      email: form.email.trim(),
      roleName: form.roleName.trim(),
      permissions,
      // Send the selected scope regardless of SE permissions
      scope: form.scope,
    };
    if (form.password.trim()) payload.password = form.password.trim();
    setSaving(true);
    try {
      if (form._id) {
        await editRolePermission(payload);
        toastMessage("Role updated successfully.", "success");
      } else {
        await createRolePermission(payload);
        toastMessage("Role created successfully.", "success");
      }
      setDialogOpen(false);
      loadRoles();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to save role.";
      toastMessage(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteRolePermission(confirmDelete._id);
      toastMessage("Role deleted.", "success");
      setConfirmDelete(null);
      loadRoles();
    } catch (err) {
      toastMessage("Failed to delete role.", "error");
    }
  };

  const scopeSummary = (scope) => {
    if (!scope) return "—";
    if (scope.allZones && scope.allRegions) return "All zones & regions";
    const parts = [];
    if (scope.allZones) parts.push("All zones");
    else if (scope.zones?.length) parts.push(`Zones: ${scope.zones.map(zoneLabel).join(", ")}`);
    if (scope.allRegions) parts.push("All regions");
    else if (scope.regions?.length) parts.push(`Regions: ${scope.regions.join(", ")}`);
    return parts.length ? parts.join(" · ") : "Unrestricted";
  };

  const columns = useMemo(
    () => [
      { field: "SAP_ID", headerName: "SAP ID", width: 120 },
      { field: "email", headerName: "Email", width: 220 },
      { field: "roleName", headerName: "Role", width: 150 },
      {
        field: "scope",
        headerName: "Data Scope",
        width: 280,
        sortable: false,
        valueGetter: (params) => scopeSummary(params.row?.scope),
      },
      {
        field: "permissions",
        headerName: "Permissions",
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Chip size="small" label={`${(params.row?.permissions || []).length} resource(s)`} />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Box>
            <IconButton size="small" onClick={() => openEdit(params.row)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => setConfirmDelete(params.row)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    []
  );

  return (
    <>
      <PageHeader title={title} subtitle={subtitle} />

      <SectionCard
        title="Roles"
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              size="small"
              placeholder="Search SAP ID / email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}>
              Add Role
            </Button>
          </Box>
        }
      >
        <ThemedDataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row._id}
          loading={loading}
          paginationMode="server"
          rowCount={total}
          page={page}
          onPageChange={(p) => setPage(p)}
          pageSize={pageSize}
          onPageSizeChange={(s) => setPageSize(s)}
          rowsPerPageOptions={[10, 25, 50]}
          height={560}
        />
      </SectionCard>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">{form._id ? "Edit Role" : "Add Role"}</Typography>
          <IconButton onClick={() => setDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="SAP ID"
                value={form.SAP_ID}
                disabled={!!form._id}
                onChange={(e) => setForm((f) => ({ ...f, SAP_ID: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Role Name"
                value={form.roleName}
                onChange={(e) => setForm((f) => ({ ...f, roleName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="password"
                label={form._id ? "New Password (optional)" : "Password"}
                value={form.password}
                autoComplete="new-password"
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              />
            </Grid>
          </Grid>

          {/* Data scope - applicable to all roles */}
          <>
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            SE Dashboard Data Scope (leave empty for all zones &amp; regions)
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth disabled={form.scope.allZones}>
                <InputLabel>Zones</InputLabel>
                <Select
                  multiple
                  label="Zones"
                  value={form.scope.zones}
                  onChange={(e) => setScope({ zones: e.target.value })}
                  renderValue={(sel) => sel.map(zoneLabel).join(", ")}
                >
                  {zoneOptions.map((z) => (
                    <MenuItem key={z} value={z}>
                      <Checkbox checked={form.scope.zones.indexOf(z) > -1} />
                      {zoneLabel(z)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.scope.allZones}
                    onChange={(e) => setScope({ allZones: e.target.checked, zones: [] })}
                  />
                }
                label="All zones"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth disabled={form.scope.allRegions}>
                <InputLabel>Regions</InputLabel>
                <Select
                  multiple
                  label="Regions"
                  value={form.scope.regions}
                  onChange={(e) => setScope({ regions: e.target.value })}
                  renderValue={(sel) => {
                    const nameOf = (code) =>
                      regionOptions.find((r) => r.regionCode === code)?.regionName || code;
                    return sel.map(nameOf).join(", ");
                  }}
                >
                  {regionOptions.map((r) => (
                    <MenuItem key={r.regionCode} value={r.regionCode}>
                      <Checkbox checked={form.scope.regions.indexOf(r.regionCode) > -1} />
                      {r.regionName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.scope.allRegions}
                    onChange={(e) => setScope({ allRegions: e.target.checked, regions: [] })}
                  />
                }
                label="All regions"
              />
            </Grid>
          </Grid>
          </>

          {/* Feature permissions */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Feature Permissions (grant at least one)
          </Typography>
          <Box sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 1, p: 1 }}>
            <Grid container sx={{ fontWeight: 600, px: 1, py: 0.5 }}>
              <Grid item xs={8}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>Resource</Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>View</Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>Edit</Typography>
              </Grid>
            </Grid>
            {RESOURCE_OPTIONS.map((opt) => {
              const key = pageResource(opt);
              const perm = form.permissions[key] || { view: false, edit: false };
              return (
                <Grid container key={key} alignItems="center" sx={{ px: 1 }}>
                  <Grid item xs={8}>
                    <Typography variant="body2">{opt.label}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Checkbox
                      size="small"
                      checked={perm.view}
                      onChange={() => togglePermission(key, "view")}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Checkbox
                      size="small"
                      checked={perm.edit}
                      onChange={() => togglePermission(key, "edit")}
                    />
                  </Grid>
                </Grid>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove the role assignment for <b>{confirmDelete?.SAP_ID}</b> ({confirmDelete?.email})?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RolesManager;
